import React, { FC, PropsWithChildren, RefObject, useEffect, useRef } from 'react'

interface TouchCoordinates {
  x: number
  y: number
}

export interface SwipeDetectorProps {
  /**
   * Target element which events should be attached to.
   */
  // Allow 'any' to avoid dependency to specific HTML element types
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventTarget: RefObject<any>
  /** Swipe deadzone in pixels, used to avoid accidental swipes. Defaults to 40 */
  deadzone?: number
  /** Callback triggered when swiping up */
  onSwipeUp?: (e?: TouchEvent) => void
  /** Callback triggered when swiping down */
  onSwipeDown?: (e?: TouchEvent) => void
  /** Callback triggered when swiping left */
  onSwipeLeft?: (e?: TouchEvent) => void
  /** Callback triggered when swiping right */
  onSwipeRight?: (e?: TouchEvent) => void
  /**
   * Block window scroll when swiping, to avoid intefering with desired interaction.
   * Defaults to 'false' as this may have a perfomance impact.
   */
  blockScroll?: boolean
}

export const SwipeDetector: FC<PropsWithChildren<SwipeDetectorProps>> = ({
  eventTarget,
  deadzone = 40,
  onSwipeUp,
  onSwipeDown,
  onSwipeLeft,
  onSwipeRight,
  blockScroll = false,
  children,
}) => {
  const touchStart = useRef<TouchCoordinates | null>(null)
  const touchEnd = useRef<TouchCoordinates | null>(null)

  useEffect(() => {
    if (!eventTarget.current) {
      return
    }

    const onTouchStart = (e: TouchEvent) => {
      if (blockScroll) {
        e.preventDefault()
      }

      if (!e.touches?.length) {
        return
      }

      touchStart.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      }
    }

    const onTouchMove = (e: TouchEvent) => {
      if (!e.touches?.length) {
        return
      }

      touchEnd.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      }
    }

    const onTouchEnd = (e: TouchEvent) => {
      if (!touchStart?.current || !touchEnd?.current) {
        return
      }

      const deltaX = touchStart.current.x - touchEnd.current.x
      const deltaY = touchStart.current.y - touchEnd.current.y

      // Make sure that X-axis callbacks and Y-axis callbacks
      // don't trigger simultaneously
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        if (deltaX > deadzone && onSwipeLeft) {
          onSwipeLeft(e)
        } else if (deltaX < -deadzone && onSwipeRight) {
          onSwipeRight(e)
        }
      } else {
        if (deltaY > deadzone && onSwipeUp) {
          onSwipeUp(e)
        } else if (deltaY < -deadzone && onSwipeDown) {
          onSwipeDown(e)
        }
      }

      // Reset/cleanup
      touchStart.current = null
      touchEnd.current = null
    }

    const target = eventTarget.current

    target.addEventListener('touchstart', onTouchStart, { passive: !blockScroll })
    target.addEventListener('touchmove', onTouchMove, { passive: !blockScroll })
    target.addEventListener('touchend', onTouchEnd, { passive: !blockScroll })

    return () => {
      target.removeEventListener('touchstart', onTouchStart)
      target.removeEventListener('touchmove', onTouchMove)
      target.removeEventListener('touchend', onTouchEnd)
    }
  }, [
    blockScroll,
    deadzone,
    eventTarget,
    onSwipeDown,
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
  ])

  return <>{children}</>
}
